import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';
import type { UIViewInjectedProps } from '@uirouter/react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Result,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Table,
  Typography,
} from 'antd';
import moment, { type Moment } from 'moment';
import { campaignStore, dashboardGlobalStore, productStore } from '@/stores';

import { mainMidRangesFromCampaign } from './dashboard-campaign-ranges';
import { SplitPieChart } from './pie-chart';
import { productColumns, harborColumns, consortiumColumns, toDataSource } from './setup';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

export const DashboardGlobalPage = observer(({ transition }: UIViewInjectedProps) => {
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const [productIds, setProductIds] = useState<number[] | undefined>(undefined);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | undefined>(undefined);

  // Effect for initial data load and subsequent filter changes
  useEffect(() => {
    // Don't fetch until the initial date range is set
    if (!dateRange) {
      return;
    }
    const params: cyrian.api.dashboardGlobal.GetParams = {
      startDate: dateRange[0].format('YYYY-MM-DD'),
      endDate: dateRange[1].format('YYYY-MM-DD'),
    };
    if (productIds && productIds.length > 0) {
      params.productIds = productIds;
    }
    dashboardGlobalStore.fetchData(params);
  }, [dateRange, productIds]); // Re-run effect when dateRange or productIds change

  // Effect for setting the initial date range
  useEffect(() => {
    const today = moment();
    let startOfCocoaYear: Moment;

    if (today.month() >= 9) {
      startOfCocoaYear = moment().month(9).date(1).startOf('day');
    } else {
      startOfCocoaYear = moment().subtract(1, 'year').month(9).date(1).startOf('day');
    }
    setDateRange([startOfCocoaYear, today]);
  }, []);

  // Effect to load products for the filter
  useEffect(() => {
    productStore.ensureData();
  }, []);

  useEffect(() => {
    campaignStore.ensureData({ sort: 'id:-1', limit: 200 }).catch(() => undefined);
  }, []);

  const campaigns = campaignStore.items;
  const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId);

  /** Do not pre-select a campaign: the chart uses the date range above only. Main/Mid apply that campaign's windows after an explicit choice (avoids “something selected but not applied”). */
  useEffect(() => {
    if (campaigns.length === 0 || selectedCampaignId == null) {
      return;
    }
    const stillValid = campaigns.some((c) => c.id === selectedCampaignId);
    if (!stillValid) {
      setSelectedCampaignId(undefined);
    }
  }, [campaigns, selectedCampaignId]);

  const handleDateChange = (dates: any, _dateStrings: [string, string]) => {
    setDateRange(dates);
  };

  const handleProductChange = (selectedIds: number[]) => {
    setProductIds(selectedIds);
  };

  const handleQuickFilter = (range: [Moment, Moment]) => {
    setDateRange(range);
  };

  const handleCurrentWeek = () => handleQuickFilter([moment().startOf('week'), moment().endOf('week')]);
  const handleCurrentMonth = () => handleQuickFilter([moment().startOf('month'), moment().endOf('month')]);

  const midCropYear = useMemo(() => {
    if (selectedCampaign) {
      const { mid } = mainMidRangesFromCampaign(selectedCampaign);
      return mid[0].year();
    }
    const today = moment();
    const currentMonth = today.month();
    return currentMonth < 3 ? today.year() - 1 : today.year();
  }, [selectedCampaign]);

  const handleMainCrop = () => {
    if (selectedCampaign) {
      const { main } = mainMidRangesFromCampaign(selectedCampaign);
      handleQuickFilter([main[0], main[1]]);
      return;
    }
    const today = moment();
    const start =
      today.month() >= 9
        ? moment().month(9).date(1).startOf('day')
        : moment().subtract(1, 'year').month(9).date(1).startOf('day');
    handleQuickFilter([start, today]);
  };

  const handleMidCrop = () => {
    if (selectedCampaign) {
      const { mid } = mainMidRangesFromCampaign(selectedCampaign);
      handleQuickFilter([mid[0], mid[1]]);
      return;
    }
    const range: [Moment, Moment] = [
      moment().year(midCropYear).month(3).date(1),
      moment().year(midCropYear).month(8).date(30),
    ];
    handleQuickFilter(range);
  };

  const handleRefresh = () => {
    dashboardGlobalStore.refreshData();
  };

  const { data, loading } = dashboardGlobalStore;

  const productDataSource = data?.productSplitWeight ? toDataSource(data.productSplitWeight) : [];
  const harborDataSource = data?.harborSplitWeight ? toDataSource(data.harborSplitWeight) : [];
  const consortiumDataSource = data?.consortiumSplitWeight ? toDataSource(data.consortiumSplitWeight) : [];

  const openWaybillsFilteredByProduct = (productName: string) => {
    if (!dateRange) {
      return;
    }
    transition.router.stateService.go(
      'base-layout.waybills',
      {
        productName,
        consortiumName: null,
        weighingAnalysisStartDate: dateRange[0].format('YYYY-MM-DD'),
        weighingAnalysisEndDate: dateRange[1].format('YYYY-MM-DD'),
      },
      { inherit: false },
    );
  };

  /** Must match backend dashboard SQL: COALESCE(consortium.name, 'Unknown'). */
  const UNKNOWN_CONSORTIUM_BUCKET = 'Unknown';

  const openWaybillsFilteredByConsortium = (consortiumName: string) => {
    if (!dateRange) {
      return;
    }
    const isUnknownBucket = consortiumName === UNKNOWN_CONSORTIUM_BUCKET;
    transition.router.stateService.go(
      'base-layout.waybills',
      {
        productName: null,
        consortiumName: isUnknownBucket ? null : consortiumName,
        missingCooperativeConsortium: isUnknownBucket ? true : null,
        weighingAnalysisStartDate: dateRange[0].format('YYYY-MM-DD'),
        weighingAnalysisEndDate: dateRange[1].format('YYYY-MM-DD'),
      },
      { inherit: false },
    );
  };

  return (
    <>
      <Title level={2}>
        <FormattedMessage id="menu.dashboard-global" />
      </Title>

      <Card
        size="small"
        title={<FormattedMessage id="dashboard.filters.cardTitle" />}
        style={{ marginBottom: 24 }}
        bodyStyle={{ paddingTop: 16 }}
      >
        <div style={{ marginBottom: 20 }}>
          <Text strong>
            <FormattedMessage id="dashboard.filters.periodTitle" />
          </Text>
          <div style={{ marginTop: 8 }}>
            <Space wrap align="start" size="middle">
              <RangePicker value={dateRange} onChange={handleDateChange} style={{ width: '100%', maxWidth: 360 }} />
              <Button onClick={handleCurrentWeek}>
                <FormattedMessage id="dashboard.quickFilter.week" />
              </Button>
              <Button onClick={handleCurrentMonth}>
                <FormattedMessage id="dashboard.quickFilter.month" />
              </Button>
            </Space>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Text strong>
            <FormattedMessage id="dashboard.filters.productsTitle" />
          </Text>
          <div style={{ marginTop: 8 }}>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', maxWidth: 480 }}
              placeholder={<FormattedMessage id="dashboard.filter.products" />}
              onChange={handleProductChange}
              options={productStore.items.map((p) => ({ label: p.name, value: p.id }))}
              loading={productStore.loading}
              value={productIds}
            />
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        <div>
          <Text strong>
            <FormattedMessage id="dashboard.filters.campaignSeasonTitle" />
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 6, maxWidth: 560 }}>
            <FormattedMessage id="dashboard.filters.campaignSeasonHint" />
          </Paragraph>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={10} lg={8}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                style={{ width: '100%' }}
                placeholder={<FormattedMessage id="dashboard.filter.campaign" />}
                onChange={(id) => setSelectedCampaignId(id as number | undefined)}
                options={campaigns.map((c) => ({
                  label: `${c.name} (${c.year})`,
                  value: c.id,
                }))}
                loading={campaignStore.loading}
                value={selectedCampaignId}
              />
            </Col>
            <Col xs={24} sm={24} md={14} lg={16}>
              <Space wrap>
                <Button onClick={handleMainCrop} disabled={Boolean(campaigns.length > 0 && selectedCampaignId == null)}>
                  <FormattedMessage id="dashboard.quickFilter.mainCrop" />
                </Button>
                <Button onClick={handleMidCrop} disabled={Boolean(campaigns.length > 0 && selectedCampaignId == null)}>
                  <FormattedMessage id="dashboard.quickFilter.midCrop" values={{ year: midCropYear }} />
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      </Card>

      <Spin spinning={loading} size="large">
        {data ? (
          <div style={{ padding: '24px 0', minHeight: 200 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic
                    title={<FormattedMessage id="dashboard.totalAcceptedWeight" />}
                    value={data.totalAcceptedWeight != null ? data.totalAcceptedWeight / 1000 : 0}
                    precision={2}
                    suffix="MT"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic
                    title={<FormattedMessage id="dashboard.totalAcceptedBags" />}
                    value={data.totalAcceptedBags}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic
                    title={<FormattedMessage id="dashboard.totalLotWeight" />}
                    value={data.totalLotWeight != null ? data.totalLotWeight / 1000 : 0}
                    precision={2}
                    suffix="MT"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic title={<FormattedMessage id="dashboard.totalLotsCount" />} value={data.totalLotsCount} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic title={<FormattedMessage id="dashboard.farmersCount" />} value={data.farmersCount} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic
                    title={<FormattedMessage id="dashboard.cooperativesCount" />}
                    value={data.cooperativesCount}
                  />
                </Card>
              </Col>
            </Row>

            {(data.totalAcceptedWeight == null || data.totalAcceptedWeight === 0) && (data.totalLotsCount ?? 0) > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 16 }}
                message={<FormattedMessage id="dashboard.waybillKpiIncomplete" />}
              />
            )}

            <Row style={{ marginTop: 24 }}>
              <Col span={24}>
                <Card title={<FormattedMessage id="dashboard.productSplit" />}>
                  {productDataSource.length === 0 ? (
                    <Empty description={<FormattedMessage id="dashboard.emptyProductSplit" />} />
                  ) : (
                    <Row gutter={[24, 16]} align="middle">
                      <Col xs={24} md={10} lg={9}>
                        <SplitPieChart data={productDataSource} onSliceClick={openWaybillsFilteredByProduct} />
                      </Col>
                      <Col xs={24} md={14} lg={15}>
                        <Table
                          dataSource={productDataSource}
                          columns={productColumns}
                          pagination={false}
                          size="small"
                          onRow={(record) => ({
                            onClick: () => openWaybillsFilteredByProduct(record.name),
                            className: 'table-row-cursor',
                          })}
                        />
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
            </Row>
            <Row style={{ marginTop: 24 }}>
              <Col span={24}>
                <Card title={<FormattedMessage id="dashboard.harborSplit" />}>
                  {harborDataSource.length === 0 ? (
                    <Empty description={<FormattedMessage id="dashboard.emptyHarborSplit" />} />
                  ) : (
                    <Row gutter={[24, 16]} align="middle">
                      <Col xs={24} md={10} lg={9}>
                        <SplitPieChart data={harborDataSource} />
                      </Col>
                      <Col xs={24} md={14} lg={15}>
                        <Table dataSource={harborDataSource} columns={harborColumns} pagination={false} size="small" />
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
            </Row>
            <Row style={{ marginTop: 24 }}>
              <Col span={24}>
                <Card title={<FormattedMessage id="dashboard.consortiumSplit" />}>
                  {consortiumDataSource.length === 0 ? (
                    <Empty description={<FormattedMessage id="dashboard.emptyConsortiumSplit" />} />
                  ) : (
                    <Row gutter={[24, 16]} align="middle">
                      <Col xs={24} md={10} lg={9}>
                        <SplitPieChart data={consortiumDataSource} onSliceClick={openWaybillsFilteredByConsortium} />
                      </Col>
                      <Col xs={24} md={14} lg={15}>
                        <Table
                          dataSource={consortiumDataSource}
                          columns={consortiumColumns}
                          pagination={false}
                          size="small"
                          onRow={(record) => ({
                            onClick: () => openWaybillsFilteredByConsortium(record.name),
                            className: 'table-row-cursor',
                          })}
                        />
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        ) : loading ? (
          // Initial loading state: show a skeleton to define the space.
          <div style={{ padding: 24 }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        ) : (
          // Error state: loading is false and still no data.
          <Result
            status="warning"
            title={<FormattedMessage id="dashboard.error.title" />}
            subTitle={<FormattedMessage id="dashboard.error.subtitle" />}
            extra={
              <Button type="primary" onClick={handleRefresh}>
                <FormattedMessage id="dashboard.tryAgain" />
              </Button>
            }
          />
        )}
      </Spin>
    </>
  );
});
